import { supabase } from '@/integrations/supabase/client';

export type StorageBucket = 'profile-images' | 'bulletin-images' | 'relic-images' | 'videos' | 'video-thumbnails' | 'drop-assets';

export interface OrganizedUploadOptions {
  file: File;
  userId: string;
  bucket: StorageBucket;
  type?: 'avatar' | 'banner' | 'signature' | 'bulletin' | 'relic' | 'video' | 'thumbnail' | 'drop';
  contentType?: string;
  maxSize?: number;
  // New: drop-specific organization
  collectionId?: string;
  rarity?: 'common' | 'uncommon' | 'rare' | 'legendary';
}

export class StorageManager {
  private static getOrganizedPath(userId: string, bucket: StorageBucket, type?: string, filename?: string): string {
    const now = new Date();
    const yearMonth = `${now.getFullYear()}-${String(now.getMonth() + 1).padStart(2, '0')}`;
    const timestamp = Date.now();
    
    switch (bucket) {
      case 'profile-images':
        // Profile images: profile-images/{userId}/{type}.{ext}
        return `${userId}/${type || 'image'}.${filename?.split('.').pop() || 'jpg'}`;
      
      case 'bulletin-images':
      case 'relic-images':
        // Bulletins/Relics: {bucket}/{userId}/{year-month}/{timestamp}-{filename}
        return `${userId}/${yearMonth}/${timestamp}-${filename}`;
      
      case 'videos':
        // Videos: videos/{userId}/{year-month}/{timestamp}-{filename}
        return `${userId}/${yearMonth}/${timestamp}-${filename}`;
      
      case 'video-thumbnails':
        // Thumbnails: video-thumbnails/{userId}/thumbnails/{filename}
        return `${userId}/thumbnails/${filename}`;
      
      case 'drop-assets':
        // Drop assets: drop-assets/{userId}/{year-month}/{timestamp}-{filename}
        return `${userId}/${yearMonth}/${timestamp}-${filename}`;
      
      default:
        // Fallback to old structure
        return `${userId}/${timestamp}-${filename}`;
    }
  }

  private static getLegacyPath(userId: string, filename: string): string {
    // Legacy path structure for backward compatibility
    return `${userId}/${Date.now()}-${filename}`;
  }

  static async uploadWithOrganization(options: OrganizedUploadOptions) {
    const { file, userId, bucket, type, contentType, maxSize = 25 * 1024 * 1024 } = options;

    // Validate file
    if (!(file instanceof File)) {
      throw new Error('Invalid file object');
    }

    if (file.size === 0) {
      throw new Error('File is empty');
    }

    if (file.size > maxSize) {
      throw new Error(`File size too large. Maximum size is ${Math.round(maxSize / (1024 * 1024))}MB`);
    }

    // Sanitize filename
    const sanitizedFilename = file.name
      .replace(/[^a-zA-Z0-9.-]/g, '_')
      .replace(/_{2,}/g, '_');

    // Generate organized path
    let filePath: string;
    if (
      bucket === 'drop-assets' &&
      type === 'drop' &&
      options.collectionId &&
      options.rarity
    ) {
      // Required new structure: {collection_id}/{rarity}/{timestamp}-filename
      const timestamp = Date.now();
      filePath = `${options.collectionId}/${options.rarity}/${timestamp}-${sanitizedFilename}`;
    } else {
      filePath = this.getOrganizedPath(userId, bucket, type, sanitizedFilename);
    }

    // Determine content type
    const finalContentType = contentType || file.type || this.getMimeTypeFromExtension(sanitizedFilename);

    console.log('Organized upload:', {
      bucket,
      filePath,
      contentType: finalContentType,
      fileSize: file.size
    });

    // Upload file
    const { data, error } = await supabase.storage
      .from(bucket)
      .upload(filePath, file, {
        contentType: finalContentType,
        upsert: true
      });

    if (error) {
      console.error('Upload error:', error);
      throw error;
    }

    // Prefer signed URL for private buckets, fallback to public URL
    let url: string | null = null;
    try {
      const { data: signed } = await supabase.storage
        .from(bucket)
        .createSignedUrl(filePath, 60 * 60 * 24 * 30); // 30 days
      url = signed?.signedUrl || null;
    } catch (e) {
      console.warn('Signed URL generation failed, falling back to public URL');
    }

    if (!url) {
      const { data: pub } = supabase.storage
        .from(bucket)
        .getPublicUrl(filePath);
      url = pub.publicUrl;
    }

    return { data, publicUrl: url, filePath };
  }

  static async moveFileToOrganizedStructure(
    oldPath: string, 
    bucket: StorageBucket, 
    userId: string, 
    type?: string
  ): Promise<string | null> {
    try {
      // Download the file from old location
      const { data: fileData, error: downloadError } = await supabase.storage
        .from(bucket)
        .download(oldPath);

      if (downloadError || !fileData) {
        console.warn(`Could not download file from ${oldPath}:`, downloadError);
        return null;
      }

      // Extract filename from old path
      const filename = oldPath.split('/').pop() || 'file';
      
      // Generate new organized path
      const newPath = this.getOrganizedPath(userId, bucket, type, filename);

      // Upload to new location
      const { error: uploadError } = await supabase.storage
        .from(bucket)
        .upload(newPath, fileData, {
          contentType: this.getMimeTypeFromExtension(filename),
          upsert: true
        });

      if (uploadError) {
        console.error(`Failed to upload file to new location ${newPath}:`, uploadError);
        return null;
      }

      // Delete old file
      const { error: deleteError } = await supabase.storage
        .from(bucket)
        .remove([oldPath]);

      if (deleteError) {
        console.warn(`Failed to delete old file ${oldPath}:`, deleteError);
      }

      console.log(`Successfully moved file from ${oldPath} to ${newPath}`);
      return newPath;

    } catch (error) {
      console.error(`Error moving file ${oldPath}:`, error);
      return null;
    }
  }

  static async moveFileToDropPath(
    oldPath: string,
    bucket: StorageBucket,
    collectionId: string,
    rarity: 'common' | 'uncommon' | 'rare' | 'legendary'
  ): Promise<string | null> {
    try {
      const { data: fileData, error: downloadError } = await supabase.storage
        .from(bucket)
        .download(oldPath);

      if (downloadError || !fileData) {
        console.warn(`Could not download file from ${oldPath}:`, downloadError);
        return null;
      }

      const filename = oldPath.split('/').pop() || 'file';
      const timestamp = Date.now();
      const newPath = `${collectionId}/${rarity}/${timestamp}-${filename}`;

      const { error: uploadError } = await supabase.storage
        .from(bucket)
        .upload(newPath, fileData, {
          contentType: this.getMimeTypeFromExtension(filename),
          upsert: true,
        });

      if (uploadError) {
        console.error(`Failed to upload file to new location ${newPath}:`, uploadError);
        return null;
      }

      const { error: deleteError } = await supabase.storage
        .from(bucket)
        .remove([oldPath]);
      if (deleteError) {
        console.warn(`Failed to delete old file ${oldPath}:`, deleteError);
      }

      return newPath;
    } catch (error) {
      console.error('Error moving file to drop path:', error);
      return null;
    }
  }

  static extractStoragePathFromUrl(url: string, bucket: StorageBucket): string | null {
    try {
      if (!url) return null;
      const idx = url.indexOf(`/object/`);
      if (idx === -1) return null;
      // Handle /object/public/{bucket}/path OR /object/sign/{bucket}/path
      const parts = url.substring(idx).split('/');
      // parts: ['object', 'public'|'sign', '{bucket}', ...path]
      const bucketIndex = parts.findIndex((p) => p === bucket);
      if (bucketIndex === -1) return null;
      const pathParts = parts.slice(bucketIndex + 1);
      return decodeURIComponent(pathParts.join('/')) || null;
    } catch (e) {
      return null;
    }
  }

  static resolveUrl(url: string): string {
    // Handle both old and new URL formats
    if (!url) return '';
    
    // If it's already a full URL, return as-is
    if (url.startsWith('http')) return url;
    
    // If it's a path, extract bucket and path for URL generation
    const parts = url.split('/');
    if (parts.length < 2) return url;
    
    // This method handles backward compatibility
    return url;
  }

  static async cleanupUserFiles(userId: string, bucket: StorageBucket): Promise<number> {
    try {
      // List all files for the user
      const { data: files, error } = await supabase.storage
        .from(bucket)
        .list(userId, { limit: 1000 });

      if (error || !files) {
        console.error(`Error listing files for user ${userId}:`, error);
        return 0;
      }

      // Delete all files
      const filePaths = files.map(file => `${userId}/${file.name}`);
      
      if (filePaths.length === 0) return 0;

      const { error: deleteError } = await supabase.storage
        .from(bucket)
        .remove(filePaths);

      if (deleteError) {
        console.error(`Error deleting files for user ${userId}:`, deleteError);
        return 0;
      }

      console.log(`Cleaned up ${filePaths.length} files for user ${userId} in ${bucket}`);
      return filePaths.length;

    } catch (error) {
      console.error(`Error cleaning up user files:`, error);
      return 0;
    }
  }

  static getUserStorageStats(userId: string, bucket: StorageBucket) {
    // This would typically require a database query or storage API call
    // For now, return a placeholder that can be implemented later
    return {
      totalFiles: 0,
      totalSize: 0,
      lastUpload: null
    };
  }

  private static getMimeTypeFromExtension(filename: string): string {
    const lastDotIndex = filename.lastIndexOf('.');
    if (lastDotIndex === -1) return 'application/octet-stream';
    
    const extension = filename.slice(lastDotIndex + 1).toLowerCase();
    
    const typeMap: Record<string, string> = {
      jpg: 'image/jpeg',
      jpeg: 'image/jpeg',
      png: 'image/png',
      gif: 'image/gif',
      webp: 'image/webp',
      svg: 'image/svg+xml',
      heic: 'image/heic',
      mp4: 'video/mp4',
      mov: 'video/quicktime',
      avi: 'video/x-msvideo',
      webm: 'video/webm'
    };

    return typeMap[extension] || 'application/octet-stream';
  }
}